import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Link2 } from "lucide-react";

export const DOC_TYPES = ["NDA", "MSA", "SOW", "Proposal", "Other"];
export const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "awaiting_signature", label: "Awaiting signature" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
];

const inputCls = "h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm";

export default function FileDocumentDialog({ open, onOpenChange, onDone, fixedOrgId, fixedOppId, fixedCounterpartyName, orgs = [] }) {
  const empty = { mode: "upload", name: "", doc_type: "NDA", counterparty_name: "", counterparty_org_id: "", effective_date: "", expiry_date: "", status: "draft", url: "" };
  const [f, setF] = useState(empty);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setF(empty); setFile(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = (f.mode === "upload" ? !!file : /^https?:\/\//i.test(f.url.trim())) && !saving;

  const pickOrg = (id) => {
    const o = orgs.find((x) => x.id === id);
    setF((cur) => ({ ...cur, counterparty_org_id: id, counterparty_name: cur.counterparty_name || (o?.name || "") }));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const meta = {
        name: f.name.trim(), doc_type: f.doc_type, counterparty_name: (fixedCounterpartyName || f.counterparty_name).trim(),
        counterparty_org_id: fixedOrgId || f.counterparty_org_id || "", effective_date: f.effective_date || "",
        expiry_date: f.expiry_date || "", status: f.status,
        organization_id: fixedOrgId || f.counterparty_org_id || "", opportunity_id: fixedOppId || "",
      };
      let data;
      if (f.mode === "upload") {
        const fd = new FormData();
        fd.append("file", file);
        Object.entries(meta).forEach(([k, v]) => { if (v) fd.append(k, v); });
        ({ data } = await api.post("/documents/upload", fd));
      } else {
        ({ data } = await api.post("/documents/link", { url: f.url.trim(), ...meta }));
      }
      toast.success("Document filed");
      onOpenChange(false);
      onDone?.(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="file-document-dialog">
        <DialogHeader><DialogTitle className="font-heading">File a document</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-2" data-testid="doc-source-toggle">
            {[["upload", "Upload a file", Upload], ["link", "Paste a link", Link2]].map(([v, label, Icon]) => (
              <button type="button" key={v} onClick={() => setF({ ...f, mode: v })} className={`flex items-center gap-2 p-3 rounded-xl border text-sm transition-colors ${f.mode === v ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`} data-testid={`doc-source-${v}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          {f.mode === "upload" ? (
            <div><Label>File</Label><input type="file" onChange={(e) => { setFile(e.target.files?.[0] || null); if (e.target.files?.[0] && !f.name) setF((c) => ({ ...c, name: e.target.files[0].name })); }} className="mt-1 block w-full text-sm" data-testid="doc-file-input" /><p className="text-xs text-slate-400 mt-1">Up to 25 MB. Upload the already-signed copy.</p></div>
          ) : (
            <div><Label>Link</Label><Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://..." data-testid="doc-url-input" /></div>
          )}
          <div><Label>Document name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Mutual NDA - Acme" data-testid="doc-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label><select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className={inputCls} data-testid="doc-type-select">{DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
            <div><Label>Status</Label><select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls} data-testid="doc-status-select">{STATUS_OPTIONS.filter((s) => s.value !== "expired").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
          </div>
          {!fixedOrgId && (
            <div>
              <Label>Counterparty</Label>
              <Input value={f.counterparty_name} onChange={(e) => setF({ ...f, counterparty_name: e.target.value })} placeholder="Company or person name" data-testid="doc-counterparty-input" />
              {orgs.length > 0 && (
                <select value={f.counterparty_org_id} onChange={(e) => pickOrg(e.target.value)} className={`${inputCls} mt-2`} data-testid="doc-counterparty-org-select">
                  <option value="">Not an existing company (free text above)</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Effective date</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="doc-effective-date" /></div>
            <div><Label>Expiry / renewal</Label><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="doc-expiry-date" /></div>
          </div>
          <Button onClick={submit} disabled={!canSubmit} className="w-full rounded-full" data-testid="doc-save-btn">{saving ? "Filing..." : "File document"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
